/**
 * Designer Store - 设计师公开页面状态管理
 * @description 管理设计师列表和设计师详情（公开页面），从 Task Store 隔离
 */

import type { Designer, DesignerListParams } from '@/types/designer'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getDesignerDetail, getDesignerList } from '@/api/designers'

// 默认设计师详情数据（防止模板渲染错误）
const defaultDesignerDetail: Designer = {
  id: '',
  name: '',
  avatar: '',
  userType: 'individual',
  gender: 'unknown',
  age: 0,
  education: '',
  province: '',
  city: '',
  introduction: '',
  creditScore: 0,
  stats: {
    orderCount: 0,
    successRate: 0,
    successOrderCount: 0,
  },
  deposit: 0,
  skills: [],
  software: [],
  workExperiences: [],
  projects: [],
}

export const useDesignerStore = defineStore('designer-v2', () => {
  // ==================== State ====================

  /** 当前设计师详情 */
  const currentDesigner = ref<Designer>({ ...defaultDesignerDetail })
  /** 设计师详情加载状态 */
  const designerLoading = ref(false)
  /** 设计师列表 */
  const designerList = ref<Designer[]>([])
  /** 设计师列表总数 */
  const designerTotal = ref(0)
  /** 列表加载状态 */
  const loading = ref(false)
  /** 错误信息 */
  const error = ref<string | null>(null)

  // ==================== Actions ====================

  /**
   * 获取设计师详情
   * @param id 设计师ID
   */
  async function fetchDesignerDetail(id: string | number) {
    designerLoading.value = true
    error.value = null

    try {
      const response = await getDesignerDetail(id)
      currentDesigner.value = { ...defaultDesignerDetail, ...response }
    }
    catch (e: unknown) {
      error.value = e instanceof Error ? e.message : '获取设计师详情失败'
      currentDesigner.value = { ...defaultDesignerDetail }
    }
    finally {
      designerLoading.value = false
    }
  }

  /**
   * 获取设计师列表
   * @param params 查询参数
   */
  async function fetchDesigners(params: DesignerListParams = {}) {
    loading.value = true
    error.value = null

    try {
      const response = await getDesignerList(params)
      designerList.value = response.items || []
      designerTotal.value = response.total || 0
    }
    catch (e: unknown) {
      error.value = e instanceof Error ? e.message : '获取设计师列表失败'
      designerList.value = []
      designerTotal.value = 0
    }
    finally {
      loading.value = false
    }
  }

  /**
   * 重置所有状态
   */
  function reset() {
    currentDesigner.value = { ...defaultDesignerDetail }
    designerLoading.value = false
    designerList.value = []
    designerTotal.value = 0
    loading.value = false
    error.value = null
  }

  return {
    // State
    currentDesigner,
    designerLoading,
    designerList,
    designerTotal,
    loading,
    error,
    // Actions
    fetchDesignerDetail,
    fetchDesigners,
    reset,
  }
})
